import { useEffect } from 'react'
import { useRouter } from 'next/router'
import type { Course } from '@mc/api'
import { useStaticData } from '@mc/static-data-react'
import { track } from '@mc/track-event'
import type {
  DynamicHeroType,
  DynamicHeroValues,
  UtmDynamicType,
} from './serverDynamicHero'

type UtmKeyToPropertyNameMapType = {
  utmPropertyIndicator: string
  propertyName: string
}

type UTMContent = ReturnType<typeof useRouter>['query'][string]

type TrackViewedCMHeaderProps = {
  title: string
  subtitle: string
  nameplate: string
  trailerMediaUuid?: string
  isDynamicHero: boolean
  primaryCategory: number
}

const utmKeyToPropertyNameMap = [
  { utmPropertyIndicator: 'head_', propertyName: 'headerKey' },
  { utmPropertyIndicator: 'subhead_', propertyName: 'subheaderKey' },
  { utmPropertyIndicator: 'nameplate_', propertyName: 'nameplateKey' },
  {
    utmPropertyIndicator: 'trailerMediaUuid_',
    propertyName: 'trailerMediaUuidKey',
  },
]

const UTM_CONTENT_SEPARATOR = /\s|\+/

export const getDynamicValuesFromUtmContent = (utmContent: UTMContent) => {
  if (!utmContent || typeof utmContent !== 'string') return null
  const keys = utmContent.split(UTM_CONTENT_SEPARATOR)

  const {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  } = keys.reduce((acc: Record<string, string | undefined>, key) => {
    const item = utmKeyToPropertyNameMap.find((f) =>
      key.startsWith(f.utmPropertyIndicator),
    ) as UtmKeyToPropertyNameMapType
    if (!item) return acc
    acc[item.propertyName] = key.replace(item.utmPropertyIndicator, '')
    return acc
  }, {})

  return {
    headerKey,
    subheaderKey,
    imageKey,
    mobileImageKey,
    nameplateKey,
    trailerMediaUuidKey,
  }
}

const useRetrieveDynamicHeroValues = (defaultValues: DynamicHeroValues) => {
  const { query } = useRouter()
  const { dynamicHero, utmDynamic } = useDynamicHeroStaticData()

  const shouldDisplayDynamicHero = useShouldDisplayCMDynamicHero()
  if (!shouldDisplayDynamicHero) return defaultValues

  const dynamicValueKeys = getDynamicValuesFromUtmContent(query?.utm_content)

  if (!objectHasAnyValues(dynamicValueKeys)) {
    // PEN-5314: if there are no dynamic value keys, use the `utm_dynamic` object
    const utmDynamicVals = utmDynamic?.[query?.utm_dynamic as string]
    return {
      ...defaultValues,
      ...utmDynamicVals,
    }
  }
  const { headerKey, subheaderKey, nameplateKey, trailerMediaUuidKey } =
    dynamicValueKeys

  const header =
    dynamicHero?.header?.[headerKey as string] || defaultValues?.header
  const subheader =
    dynamicHero?.subheader?.[subheaderKey as string] || defaultValues?.subheader
  const nameplate =
    dynamicHero?.nameplate?.[nameplateKey as string] || defaultValues?.nameplate
  const trailerMediaUuid =
    dynamicHero?.trailerMediaUuid?.[trailerMediaUuidKey as string] ||
    defaultValues?.trailerMediaUuid

  return { header, subheader, nameplate, trailerMediaUuid }
}

const useDynamicHeroStaticData = () => {
  const { course, dynamicHero, savedDynamicHero, utmDynamic } = useStaticData<{
    course: Course
    dynamicHero: DynamicHeroType | null | undefined
    utmDynamic: UtmDynamicType | null | undefined
    savedDynamicHero: DynamicHeroValues
  }>()

  return { course, dynamicHero, savedDynamicHero, utmDynamic }
}

type UseCMDynamicHeroOpts = {
  /**
   * If true, then default values fetched from the class data will be returned
   * (preserves legacy behavior).
   *
   * If false, then dynamic hero values will be returned only if they are
   * present (in the UTM query string or in the saved dynamic CM cookie).
   * This makes it easier to use custom fallbacks.
   */
  useCourseDefaultValues?: boolean
}
export const useCMDynamicHero = (
  opts: UseCMDynamicHeroOpts = { useCourseDefaultValues: false },
) => {
  const { course, dynamicHero, savedDynamicHero } = useDynamicHeroStaticData()

  let shouldDisplayDynamicHero = useShouldDisplayCMDynamicHero()

  const courseDefaultValues = {
    header: course?.headline,
    subheader: course?.marketingCmColumn,
    nameplate: course?.nameplate,
    trailerMediaUuid: course?.mediaUuid,
  }

  const defaultHeroValues = {
    ...(opts.useCourseDefaultValues ? courseDefaultValues : {}),
    ...savedDynamicHero,
  }

  const retrievedDynamicHeroValues =
    useRetrieveDynamicHeroValues(defaultHeroValues)

  if (!objectHasAnyValues(retrievedDynamicHeroValues)) {
    shouldDisplayDynamicHero = false
  }

  useEffect(() => {
    if (shouldDisplayDynamicHero) {
      trackViewedCMHeader({
        title: retrievedDynamicHeroValues?.header || '',
        subtitle: retrievedDynamicHeroValues?.subheader || '',
        nameplate: retrievedDynamicHeroValues?.nameplate || '',
        trailerMediaUuid: retrievedDynamicHeroValues?.trailerMediaUuid || '',
        isDynamicHero:
          !!dynamicHero?.header ||
          !!dynamicHero?.subheader ||
          !!dynamicHero?.nameplate ||
          !!dynamicHero?.trailerMediaUuid,
        primaryCategory: course.primaryCategory?.id as number,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!shouldDisplayDynamicHero && !opts?.useCourseDefaultValues) {
    return {}
  }

  return retrievedDynamicHeroValues
}

const useShouldDisplayCMDynamicHero = () => {
  const { query } = useRouter()
  const { savedDynamicHero, dynamicHero, utmDynamic } =
    useDynamicHeroStaticData()

  if (!dynamicHero && !utmDynamic) return false

  // return true if the `utm_content` string specifies any dynamic hero values
  const utmContentKeys = getDynamicValuesFromUtmContent(query?.utm_content)
  if (objectHasAnyValues(utmContentKeys)) {
    return true
  }

  // return true if the `utm_dynamic` object specifies any dynamic hero values
  const utmDynamicVals = utmDynamic?.[query?.utm_dynamic as string]
  if (objectHasAnyValues(utmDynamicVals)) {
    return true
  }

  // return true if there are any saved dynamic hero values (from the cookie)
  if (
    savedDynamicHero?.header ||
    savedDynamicHero?.subheader ||
    savedDynamicHero?.nameplate ||
    savedDynamicHero?.trailerMediaUuid
  )
    return true

  return false
}

export const trackViewedCMHeader = ({
  title,
  subtitle,
  nameplate,
  trailerMediaUuid,
  isDynamicHero,
  primaryCategory,
}: TrackViewedCMHeaderProps) => {
  track('Viewed CM Header', {
    title,
    subtitle,
    nameplate,
    trailerMediaUuid,
    context: {
      dynamic_header: isDynamicHero,
    },
    p1: primaryCategory,
  })
}

const objectHasAnyValues = <T>(
  obj: T | null | undefined,
): obj is NonNullable<T> => {
  if (!obj) return false
  if (typeof obj !== 'object') return false
  const values = Object.values(obj)
  return values.some((v) => typeof v === 'string' && v.length > 0)
}
